/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Renderer2
} from '@angular/core';
import {MdDatepicker} from './datepicker';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {MdInputContainer} from '../input/input-container';
import {DOWN_ARROW} from '../core/keyboard/keycodes';
import {DateAdapter} from '../core/datetime/index';
import {createMissingDateImplError} from './datepicker-errors';
import {MD_DATE_FORMATS, MdDateFormats} from '../core/datetime/date-formats';


export const /** @type {?} */ MD_DATEPICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdDatepickerInput),
  multi: true
};


export const /** @type {?} */ MD_DATEPICKER_VALIDATORS: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MdDatepickerInput),
  multi: true
};
/**
 * Directive used to connect an input to a MdDatepicker.
 */
export class MdDatepickerInput<D> implements AfterContentInit, ControlValueAccessor, OnDestroy,
    Validator {
/**
 * The datepicker that this input is associated with.
 * @param {?} value
 * @return {?}
 */
set mdDatepicker(value: MdDatepicker<D>) {
    if (value) {
      this._datepicker = value;
      this._datepicker._registerInput(this);
    }
  }
  _datepicker: MdDatepicker<D>;
/**
 * @param {?} value
 * @return {?}
 */
set matDatepicker(value: MdDatepicker<D>) { this.mdDatepicker = value; }
/**
 * @param {?} filter
 * @return {?}
 */
set mdDatepickerFilter(filter: (date: D | null) => boolean) {
    this._dateFilter = filter;
    this._validatorOnChange();
  }
  _dateFilter: (date: D | null) => boolean;
/**
 * @param {?} filter
 * @return {?}
 */
set matDatepickerFilter(filter: (date: D | null) => boolean) {
    this.mdDatepickerFilter = filter;
  }
/**
 * The value of the input.
 * @return {?}
 */
get value(): D {
    return this._dateAdapter.parse(this._elementRef.nativeElement.value,
        this._dateFormats.parse.dateInput);
  }
/**
 * @param {?} value
 * @return {?}
 */
set value(value: D) {
    let /** @type {?} */ date = this._dateAdapter.parse(value, this._dateFormats.parse.dateInput);
    let /** @type {?} */ oldDate = this.value;
    this._renderer.setProperty(this._elementRef.nativeElement, 'value',
        date ? this._dateAdapter.format(date, this._dateFormats.display.dateInput) : '');
    if (!this._dateAdapter.sameDate(oldDate, date)) {
      this._valueChange.emit(date);
    }
  }
/**
 * The minimum valid date.
 * @return {?}
 */
get min(): D { return this._min; }
/**
 * @param {?} value
 * @return {?}
 */
set min(value: D) {
    this._min = value;
    this._validatorOnChange();
  }
private _min: D;
/**
 * The maximum valid date.
 * @return {?}
 */
get max(): D { return this._max; }
/**
 * @param {?} value
 * @return {?}
 */
set max(value: D) {
    this._max = value;
    this._validatorOnChange();
  }
private _max: D;
/**
 * Emits when the value changes (either due to user input or programmatic change).
 */
_valueChange = new EventEmitter<D>();

  _onTouched = () => {};
private _cvaOnChange: (value: any) => void = () => {};
private _validatorOnChange = () => {};
private _datepickerSubscription: Subscription;
/**
 * The form control validator for the min date.
 */
private _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return (!this.min || !control.value ||
        this._dateAdapter.compareDate(this.min, control.value) <= 0) ?
        null : {'mdDatepickerMin': {'min': this.min, 'actual': control.value}};
  }
/**
 * The form control validator for the max date.
 */
private _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return (!this.max || !control.value ||
        this._dateAdapter.compareDate(this.max, control.value) >= 0) ?
        null : {'mdDatepickerMax': {'max': this.max, 'actual': control.value}};
  }
/**
 * The form control validator for the date filter.
 */
private _filterValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return !this._dateFilter || !control.value || this._dateFilter(control.value) ?
        null : {'mdDatepickerFilter': true};
  }
/**
 * The combined form control validator for this input.
 */
private _validator: ValidatorFn =
      Validators.compose([this._minValidator, this._maxValidator, this._filterValidator]);
/**
 * @param {?} _elementRef
 * @param {?} _renderer
 * @param {?} _dateAdapter
 * @param {?} _dateFormats
 * @param {?} _mdInputContainer
 */
constructor(
private _elementRef: ElementRef,
private _renderer: Renderer2,
private _dateAdapter: DateAdapter<D>,
private _dateFormats: MdDateFormats,
private _mdInputContainer: MdInputContainer) {
    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
    if (!this._dateFormats) {
      throw createMissingDateImplError('MD_DATE_FORMATS');
    }
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    if (this._datepicker) {
      this._datepickerSubscription =
          this._datepicker.selectedChanged.subscribe((selected: D) => {
            this.value = selected;
            this._cvaOnChange(selected);
          });
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._datepickerSubscription) {
      this._datepickerSubscription.unsubscribe();
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void {
    this._validatorOnChange = fn;
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors | null {
    return this._validator ? this._validator(c) : null;
  }
/**
 * Gets the element that the datepicker popup should be connected to.
 * @return {?} The element to connect the popup to.
 */
getPopupConnectionElementRef(): ElementRef {
    return this._mdInputContainer ? this._mdInputContainer.underlineRef : this._elementRef;
  }
/**
 * @param {?} value
 * @return {?}
 */
writeValue(value: D): void {
    this.value = value;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: (value: any) => void): void {
    this._cvaOnChange = fn;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
/**
 * @param {?} disabled
 * @return {?}
 */
setDisabledState(disabled: boolean): void {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', disabled);
  }
/**
 * @param {?} event
 * @return {?}
 */
_onKeydown(event: KeyboardEvent) {
    if (event.altKey && event.keyCode === DOWN_ARROW) {
      this._datepicker.open();
      event.preventDefault();
    }
  }
/**
 * @param {?} value
 * @return {?}
 */
_onInput(value: string) {
    let /** @type {?} */ date = this._dateAdapter.parse(value, this._dateFormats.parse.dateInput);
    this._cvaOnChange(date);
    this._valueChange.emit(date);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'input[mdDatepicker], input[matDatepicker]',
  providers: [MD_DATEPICKER_VALUE_ACCESSOR, MD_DATEPICKER_VALIDATORS],
  host: {
    '[attr.aria-expanded]': '_datepicker?.opened || "false"',
    '[attr.aria-haspopup]': 'true',
    '[attr.aria-owns]': '_datepicker?.id',
    '[attr.min]': 'min ? _dateAdapter.getISODateString(min) : null',
    '[attr.max]': 'max ? _dateAdapter.getISODateString(max) : null',
    '(input)': '_onInput($event.target.value)',
    '(blur)': '_onTouched()',
    '(keydown)': '_onKeydown($event)',
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_DATE_FORMATS, ] }, ]},
{type: MdInputContainer, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'mdDatepicker': [{ type: Input },],
'matDatepicker': [{ type: Input },],
'mdDatepickerFilter': [{ type: Input },],
'matDatepickerFilter': [{ type: Input },],
'value': [{ type: Input },],
'min': [{ type: Input },],
'max': [{ type: Input },],
};
}

function MdDatepickerInput_tsickle_Closure_declarations() {
/** @type {?} */
MdDatepickerInput.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDatepickerInput.ctorParameters;
/** @type {?} */
MdDatepickerInput.propDecorators;
/** @type {?} */
MdDatepickerInput.prototype._datepicker;
/** @type {?} */
MdDatepickerInput.prototype._dateFilter;
/** @type {?} */
MdDatepickerInput.prototype._min;
/** @type {?} */
MdDatepickerInput.prototype._max;
/**
 * Emits when the value changes (either due to user input or programmatic change).
 * @type {?}
 */
MdDatepickerInput.prototype._valueChange;
/** @type {?} */
MdDatepickerInput.prototype._onTouched;
/** @type {?} */
MdDatepickerInput.prototype._cvaOnChange;
/** @type {?} */
MdDatepickerInput.prototype._validatorOnChange;
/** @type {?} */
MdDatepickerInput.prototype._datepickerSubscription;
/**
 * The form control validator for the min date.
 * @type {?}
 */
MdDatepickerInput.prototype._minValidator;
/**
 * The form control validator for the max date.
 * @type {?}
 */
MdDatepickerInput.prototype._maxValidator;
/**
 * The form control validator for the date filter.
 * @type {?}
 */
MdDatepickerInput.prototype._filterValidator;
/**
 * The combined form control validator for this input.
 * @type {?}
 */
MdDatepickerInput.prototype._validator;
/** @type {?} */
MdDatepickerInput.prototype._elementRef;
/** @type {?} */
MdDatepickerInput.prototype._renderer;
/** @type {?} */
MdDatepickerInput.prototype._dateAdapter;
/** @type {?} */
MdDatepickerInput.prototype._dateFormats;
/** @type {?} */
MdDatepickerInput.prototype._mdInputContainer;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
